import './base-phone.scss';

import { CountryCode } from 'libphonenumber-js';
import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Input, {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type Value,
} from 'react-phone-number-input/input';
import labels from 'react-phone-number-input/locale/nl.json';

import { BaseInputSize } from './base-input';

const getCountryCode = (code: string) => (code === 'ZZ' ? undefined : code);

export type BasePhoneProps = {
  testId?: string;
  type?: string;
  size?: BaseInputSize;
  disabled?: boolean;
  name: string;
  value?: string;
  defaultValue?: string;
  defaultCountry?: CountryCode;
  placeholder?: string;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onChange?: (value: string, e: ChangeEvent<HTMLElement>) => void;
  onBlur?: (value: string, e: FocusEvent<HTMLElement>) => void;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  onChangeCountry?: (
    value: string | undefined,
    e: ChangeEvent<HTMLElement>
  ) => void;
};

// eslint-disable-next-line react/display-name
export const BasePhone = forwardRef<HTMLInputElement, BasePhoneProps>(
  (
    {
      testId,
      type = 'tel',
      size = BaseInputSize.DEFAULT,
      disabled = false,
      name,
      placeholder = '',
      defaultCountry = 'NL',
      onChange,
      onBlur,
      onChangeCountry,
      ...props
    }: BasePhoneProps,
    outerRef
  ) => {
    const ref = useRef<HTMLInputElement>(null);

    useImperativeHandle(outerRef, () => ref.current!, []);

    const [country, setCountry] = useState<CountryCode | undefined>();
    const [displayValue, setDisplayValue] = useState<Value>('' as Value);

    const setField = useCallback(
      (value?: string, initial = false) => {
        const parsed = parsePhoneNumber(value ?? '', {
          defaultCountry,
          extract: true,
        });


        const currentPhoneNumber = parsed?.number ?? value ?? '';
        let currentCountry: CountryCode | undefined =
          parsed?.country ?? country ?? defaultCountry;
        if (
          parsed &&
          !currentPhoneNumber.includes(`+${parsed?.countryCallingCode}`)
        ) {
          currentCountry = undefined;
        }
        setCountry(currentCountry);
        setDisplayValue(currentPhoneNumber as Value);

        if (!initial) {
          onChange?.(currentPhoneNumber, {
            currentTarget: {
              str: currentPhoneNumber,
            },
          } as unknown as ChangeEvent<HTMLInputElement>);
          onChangeCountry?.(currentCountry, {
            currentTarget: {
              str: currentCountry,
            },
          } as unknown as ChangeEvent<HTMLInputElement>);
        }
      },
      [country, defaultCountry, onChange, onChangeCountry]
    );

    useEffect(() => {
      setField(props.value, true);
    }, [props.value, setField]);

    const onHandleCountry = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        const currentCountry =
          getCountryCode(e.currentTarget.value) ?? country ?? defaultCountry;

        const newPrefix = `+${getCountryCallingCode(currentCountry as CountryCode)}`;
        if (!country) {
          setField(newPrefix);
          return;
        }

        const oldPrefix = `+${getCountryCallingCode(country as CountryCode)}`;

        const newDisplayValue = displayValue.replace(oldPrefix, newPrefix);
        if (newDisplayValue !== displayValue) {
          setField(newDisplayValue);
        } else {
          setField(newPrefix);
        }
        setCountry(currentCountry as CountryCode);
        ref?.current?.focus();
      },
      [country, defaultCountry, displayValue, setField]
    );

    const onHandleChange = useCallback(
      (value: Value) => {
        setField(value);
      },
      [setField]
    );

    const onHandleBlur = useCallback(
      (e: FocusEvent<HTMLElement>) => {
        onBlur?.((e.currentTarget as HTMLInputElement).value, e);
      },
      [onBlur]
    );

    const onPaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData
          .getData('Text').replace(/\s/g, '');
        const parsed = parsePhoneNumber(pastedText, defaultCountry);

        setField(parsed?.number);
      },
      [defaultCountry, setField]
    );

    return (
      <div className="form-phone-control">
        <select value={country ?? defaultCountry} onChange={onHandleCountry}>
          {([['ZZ'], ...getCountries()] as CountryCode[]).map(option => (
            <option key={option} value={option}>
              {labels[option as keyof typeof labels]}
            </option>
          ))}
        </select>

        <Input
          {...props}
          data-test-id={testId}
          id={name}
          type={type}
          name={name}
          value={displayValue}
          disabled={disabled}
          className={`form-control${
            size !== BaseInputSize.DEFAULT ? ` form-control--${size}` : ``
          }`}
          placeholder={placeholder}
          onChange={onHandleChange}
          onBlur={onHandleBlur}
          onPaste={onPaste}
          ref={ref}
          international
          country={country ?? defaultCountry}
          withCountryCallingCode
        />

        <span>
          <img
            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${
              country ?? defaultCountry
            }.svg`}
            alt={country ?? defaultCountry}
          />
        </span>
      </div>
    );
  }
);
